import { useMemo, useContext } from 'react';
import { useOrgaConfigurationContext } from '@/app/providers';
import { useTheme } from '@/shared/contexts/ThemeContext';
import { PersonalizationContext } from '@/shared/contexts/PersonalizationContext';

/**
 * Hook pour récupérer les styles personnalisés pour les pages de commande
 * Utilise les valeurs personnalisées de la configuration ou les valeurs par défaut du thème
 * Si des valeurs temporaires sont disponibles (depuis le formulaire d'édition), elles sont utilisées en priorité
 */
export const useOrderPersonalization = () => {
  const { configuration } = useOrgaConfigurationContext();
  const { theme } = useTheme();
  // Utiliser les valeurs temporaires si disponibles (pour prévisualisation en temps réel)
  // Utiliser useContext directement pour s'assurer que les re-renders sont déclenchés
  // Le contexte peut être undefined si le composant n'est pas dans un PersonalizationProvider
  const context = useContext(PersonalizationContext);
  const temporaryPersonalization = context?.temporaryPersonalization ?? null;

  return useMemo(() => {
    // Si des valeurs temporaires existent, les utiliser directement (priorité absolue pour prévisualisation temps réel)
    // Sinon, utiliser les valeurs sauvegardées
    const savedPersonalization = configuration?.personalization || {};

    // Si des valeurs temporaires sont disponibles, elles remplacent complètement les valeurs sauvegardées
    // pour permettre une prévisualisation en temps réel avant sauvegarde
    const personalization = temporaryPersonalization
      ? {
        // Utiliser les valeurs temporaires, en complétant avec les valeurs sauvegardées pour les champs non définis
        layout: temporaryPersonalization.layout
          ? { ...(savedPersonalization as any)?.layout, ...temporaryPersonalization.layout }
          : (savedPersonalization as any)?.layout,
        card: temporaryPersonalization.card
          ? { ...(savedPersonalization as any)?.card, ...temporaryPersonalization.card }
          : (savedPersonalization as any)?.card,
        input: temporaryPersonalization.input
          ? { ...(savedPersonalization as any)?.input, ...temporaryPersonalization.input }
          : (savedPersonalization as any)?.input,
        button: temporaryPersonalization.button
          ? { ...(savedPersonalization as any)?.button, ...temporaryPersonalization.button }
          : (savedPersonalization as any)?.button,
        text: temporaryPersonalization.text
          ? { ...(savedPersonalization as any)?.text, ...temporaryPersonalization.text }
          : (savedPersonalization as any)?.text,
        badge: temporaryPersonalization.badge
          ? { ...(savedPersonalization as any)?.badge, ...temporaryPersonalization.badge }
          : (savedPersonalization as any)?.badge,
        icon: temporaryPersonalization.icon
          ? { ...(savedPersonalization as any)?.icon, ...temporaryPersonalization.icon }
          : (savedPersonalization as any)?.icon,
        informativeAnnouncement: temporaryPersonalization.informativeAnnouncement,
        description: temporaryPersonalization.description,
      }
      : savedPersonalization;

    // Valeurs par défaut basées sur les couleurs de l'organisation si disponibles
    const orgaPrimaryColor = (configuration as any)?.primaryColor || '#9333EA';

    // Valeurs par défaut d'origine d'OrderForm (styles initiaux avant personnalisation)
    const defaultCardBg = '#FFFFFF'; // bg-white
    const defaultCardBorder = '#E5E7EB'; // border-gray-200
    const defaultCardBorderRadius = 'rounded-3xl';
    const defaultCardShadow = 'shadow-lg';

    const defaultInputBg = '#FFFFFF'; // bg-white
    const defaultInputBorder = '#D1D5DB'; // border-gray-300
    const defaultInputFocusBorder = orgaPrimaryColor; // focus:ring-primary
    const defaultInputFocusRing = orgaPrimaryColor; // focus:ring-primary
    const defaultInputText = '#111827'; // text-gray-900
    const defaultInputPlaceholder = '#9CA3AF'; // placeholder-gray-400
    const defaultInputBorderRadius = 'rounded-2xl';

    const defaultButtonPrimary = orgaPrimaryColor; // Couleur primaire de l'orga
    const defaultButtonPrimaryText = '#FFFFFF'; // text-white
    const defaultButtonPrimaryHover = orgaPrimaryColor; // Simplifié, sera ajusté par le style hover
    const defaultButtonDisabledBackground = '#D1D5DB'; // bg-gray-300
    const defaultButtonDisabledText = '#6B7280'; // text-gray-500
    const defaultButtonBorderRadius = 'rounded-2xl';

    const defaultTextPrimary = '#111827'; // text-gray-900
    const defaultTextSecondary = '#6B7280'; // text-gray-600
    const defaultTextHeading = '#111827'; // text-gray-900

    const defaultBadgeBg = `${orgaPrimaryColor}15`; // Fond léger basé sur la couleur primaire
    const defaultBadgeText = orgaPrimaryColor;
    const defaultBadgeBorderRadius = 'rounded-full';

    const defaultIconPrimary = orgaPrimaryColor; // Couleur principale des icônes
    const defaultIconSecondary = '#6B7280'; // Couleur secondaire des icônes (gris)

    const defaultLayoutBackground = '#F9FAFB'; // bg-gray-50
    const defaultLayoutBackgroundType = 'color' as const;

    return {
      layout: {
        background: (personalization as any)?.layout?.background || defaultLayoutBackground,
        backgroundType: (personalization as any)?.layout?.backgroundType || defaultLayoutBackgroundType,
      },
      card: {
        background: (personalization as any)?.card?.background || defaultCardBg,
        border: (personalization as any)?.card?.border || defaultCardBorder,
        borderRadius: (personalization as any)?.card?.borderRadius || defaultCardBorderRadius,
        shadow: (personalization as any)?.card?.shadow || defaultCardShadow,
      },
      input: {
        background: (personalization as any)?.input?.background || defaultInputBg,
        border: (personalization as any)?.input?.border || defaultInputBorder,
        focusBorder: (personalization as any)?.input?.focusBorder || defaultInputFocusBorder,
        focusRing: (personalization as any)?.input?.focusRing || defaultInputFocusRing,
        text: (personalization as any)?.input?.text || defaultInputText,
        placeholder: (personalization as any)?.input?.placeholder || defaultInputPlaceholder,
        borderRadius: (personalization as any)?.input?.borderRadius || defaultInputBorderRadius,
      },
      button: {
        primary: (personalization as any)?.button?.primary || defaultButtonPrimary,
        primaryText: (personalization as any)?.button?.primaryText || defaultButtonPrimaryText,
        primaryHover: (personalization as any)?.button?.primaryHover || defaultButtonPrimaryHover,
        disabledBackground: (personalization as any)?.button?.disabledBackground || defaultButtonDisabledBackground,
        disabledText: (personalization as any)?.button?.disabledText || defaultButtonDisabledText,
        borderRadius: (personalization as any)?.button?.borderRadius || defaultButtonBorderRadius,
      },
      text: {
        primary: (personalization as any)?.text?.primary || defaultTextPrimary,
        secondary: (personalization as any)?.text?.secondary || defaultTextSecondary,
        heading: (personalization as any)?.text?.heading || defaultTextHeading,
      },
      badge: {
        background: (personalization as any)?.badge?.background || defaultBadgeBg,
        text: (personalization as any)?.badge?.text || defaultBadgeText,
        borderRadius: (personalization as any)?.badge?.borderRadius || defaultBadgeBorderRadius,
      },
      icon: {
        primary: (personalization as any)?.icon?.primary || defaultIconPrimary,
        secondary: (personalization as any)?.icon?.secondary || defaultIconSecondary,
      },
      informativeAnnouncement: (personalization as any)?.informativeAnnouncement || (savedPersonalization as any)?.informativeAnnouncement || '',
    };
  }, [configuration, theme, temporaryPersonalization]);
};
